<template>
  <BaseButton
    :id="computedCta?.fields?.identifier"
    :key="cta?.sys?.id"
    :style="computedStyle"
    :to="props.link || computedCta?.fields?.link"
    :native-link="nativeLink"
    :presentation="computedCta?.fields?.presentation"
    :size="computedCta?.fields?.size"
    :target="computedCta?.fields?.target"
    :type="computedCta?.fields?.type"
    @click="onClick"
  >
    {{ computedCta?.fields?.label }}

    <!-- Trailing -->
    <span v-if="computedCta?.fields?.trailing" :class="$style.trailing">
      <IconLink
        v-if="computedCta?.fields?.trailing === 'link-out'"
        :class="$style.icon"
      />
      <IconArrowRight
        v-if="computedCta?.fields?.trailing === 'arrow-right'"
        :class="$style.icon"
      />
    </span>
  </BaseButton>
</template>

<script setup lang="ts">
import type { TypeCallToActionWithoutUnresolvableLinksResponse } from '~/types/contentful'
import parseCtaDataLayer from '~/utils/parseCtaDataLayer'
import resolveParentEntryTitleFromElement from '~/utils/resolveParentEntryTitleFromElement'

declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    gtag: any
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    dataLayer: any[]
  }
}

type ICallToActionFields =
  TypeCallToActionWithoutUnresolvableLinksResponse['fields']

export interface ICallToActionWithDataLayer extends ICallToActionFields {
  id: TypeCallToActionWithoutUnresolvableLinksResponse['fields']['identifier']
  to: TypeCallToActionWithoutUnresolvableLinksResponse['fields']['link']
}

const props = defineProps<{
  cta: TypeCallToActionWithoutUnresolvableLinksResponse | undefined
  dataLayer?: Record<string, unknown>
  parentEntryTitle?: string
  label?: string
  nativeLink?: boolean
  link?: string
}>()

type DataLayerObject = Record<string, unknown>
type CallToActionDataLayer =
  TypeCallToActionWithoutUnresolvableLinksResponse['fields']['dataLayer']

function isPlainObject(value: unknown): value is DataLayerObject {
  return (
    typeof value === 'object' &&
    value !== null &&
    Object.prototype.toString.call(value) === '[object Object]'
  )
}

function mergeDataLayer(
  base: DataLayerObject | undefined,
  override: DataLayerObject | undefined,
): CallToActionDataLayer {
  if (!isPlainObject(base)) return (override ?? base) as CallToActionDataLayer
  if (!isPlainObject(override)) return base as CallToActionDataLayer

  const merged: DataLayerObject = { ...base }

  for (const [key, overrideValue] of Object.entries(override)) {
    const baseValue = merged[key]
    if (isPlainObject(baseValue) && isPlainObject(overrideValue)) {
      merged[key] = mergeDataLayer(baseValue, overrideValue)
    } else {
      merged[key] = overrideValue
    }
  }

  return merged as CallToActionDataLayer
}

const configuredCta = computed(() =>
  ctaWithConfigOverride(props.cta, useRoute()),
)
const computedCta = computed(() => {
  if (!configuredCta.value) return undefined

  const baseDataLayer = isPlainObject(configuredCta.value.fields?.dataLayer)
    ? configuredCta.value.fields?.dataLayer
    : undefined
  const inlineDataLayer = isPlainObject(props.dataLayer)
    ? props.dataLayer
    : undefined
  const mergedDataLayer = mergeDataLayer(baseDataLayer, inlineDataLayer)

  return {
    ...configuredCta.value,
    fields: {
      ...configuredCta.value.fields,
      link: props.link || configuredCta.value.fields?.link,
      label: props.label || configuredCta.value.fields?.label,
      dataLayer: mergedDataLayer ?? configuredCta.value.fields?.dataLayer,
    },
  } satisfies TypeCallToActionWithoutUnresolvableLinksResponse
})
const computedStyle = computed(() => ({
  color: 'var(--button-color)',
  textDecoration: 'none',
  ...((computedCta.value?.fields?.cssOverride as
    | Record<string, string | number | undefined>
    | undefined) ?? {}),
}))

const onClick = (event: MouseEvent) => {
  if (computedCta?.value) {
    const clickedElement =
      event.currentTarget instanceof HTMLElement ? event.currentTarget : null
      
    const parentEntryTitle =
      props.parentEntryTitle?.trim() ??
      resolveParentEntryTitleFromElement(clickedElement)

    const { err, dataLayer } = parseCtaDataLayer(computedCta.value)

    const defaultEventParams = {
      link_text: props.label || computedCta.value.fields?.label,
      link_url: props.link || computedCta.value.fields?.link,
      entry_title: computedCta.value.fields?.entryTitle,
      parent_entry_title: parentEntryTitle ?? null,
    }

    if (dataLayer) {
      dataLayer.eventParams = {
        ...defaultEventParams, // defaults go first
        ...dataLayer.eventParams, // existing values override
      }

      pushToGaDataLayer(dataLayer)
    } else {
      if (err) console.warn(err)
      pushToGaDataLayer({
        event: 'ga4.trackEvent',
        eventName: 'cta_click',
        eventParams: {
          ...defaultEventParams,
        },
      })
    }
  }
}
</script>

<style lang="scss" module>
.trailing {
  margin-left: 0.5em;
}

.icon {
  width: 0.9em;
  max-width: 100%;
}
</style>
